import React, {useEffect, useState} from 'react';
import {v4 as uuid} from 'uuid';
import {
  Body,
  Button,
  designSystemToken,
  Headline,
  Icon,
} from '@lightricks/react-design-system';
import AnalyticsService from '@/services/analytics/AnalyticsService';
import {
  buttonNames,
  flowNames,
  reasons,
  screenNames,
} from '@/lib/delta/deltaConstants.js';
import {AuthMode} from '@/types/AuthModes';
import {Product} from '@/types/product';
import {ButtonAppearance} from '@/views/auth/BaseAuth';
import useTrackAuthStepEvent from '@/hooks/analytics/use-track-auth-step-event';
import useTrackButtonEvent from '@/hooks/analytics/use-track-button-event';
import useIsMobile from '@/hooks/use-is-mobile';
import Fortress, {
  FortressFlow,
  VerifyOTPAndSaveTokenCookiesResponse,
} from '../../services/fortress/Fortress';
import translate from '../../utils/translate';
import ModalAuth from '../modal-auth';
import OtpInput from '../otp-input/OtpInput';
import styles from './OtpVerificationModal.module.scss';

export type AuthStepEventData = {
  flow_id: string;
  process_id: string;
  provider: string;
  request_type: string;
  input_value: string;
};

export type OtpVerificationModalProps = {
  testID?: string;
  isOpen: boolean;
  email: string;
  setIsModalOpen: (isOpen: boolean) => void;
  authenticateCallback: (
    verifySession: VerifyOTPAndSaveTokenCookiesResponse,
    data: AuthStepEventData
  ) => void;
  mainButtonAppearance?: ButtonAppearance;
  mode: AuthMode;
  product: Product;
  fortressFlowName: FortressFlow;
  captchaToken?: string;
  refreshCaptchaToken?: (forceRefresh?: boolean) => Promise<string | null>;
};

const AUTH_MAPPINGS = {
  signup: {
    flowName: flowNames.AUTH.NEW_SIGNUP,
    screenName: screenNames.AUTH.NEW_SIGNUP,
    otpScreenName: screenNames.AUTH.NEW_SIGNUP_OTP,
    continueButtonName: buttonNames.SIGNUP.CONTINUE,
    closeModalButtonName: buttonNames.SIGNUP.CLOSE_MODAL,
    resendCodeButtonName: buttonNames.OTP_MODAL.RESEND_CODE,
  },
  login: {
    flowName: flowNames.AUTH.LOGIN,
    screenName: screenNames.LOGIN.MAIN_LOGIN_SCREEN,
    otpScreenName: screenNames.AUTH.LOGIN_OTP,
    continueButtonName: buttonNames.LOGIN.CONTINUE,
    closeModalButtonName: buttonNames.LOGIN.CLOSE_MODAL,
    resendCodeButtonName: buttonNames.OTP_MODAL.RESEND_CODE,
  },
} as const;

function OtpVerificationModal(props: OtpVerificationModalProps) {
  const {
    isOpen,
    email,
    authenticateCallback,
    setIsModalOpen,
    testID = 'otp-verification-modal',
    mainButtonAppearance = 'brand',
    mode,
    product,
    fortressFlowName,
    captchaToken,
    refreshCaptchaToken,
  } = props;
  const [error, setError] = useState('');
  const [code, setCode] = useState('');
  const [isResendCodeLoading, setIsResendCodeLoading] = useState(false);
  const [isResendCodeSuccess, setIsResendCodeSuccess] = useState(false);
  const [isLoadingCodeVerify, setIsLoadingCodeVerify] = useState(false);
  const isMobile = useIsMobile();
  const [otpInputs, setOtpInputs] = useState<HTMLInputElement[]>([]);
  const {started: trackAuthStepStarted, ended: trackAuthStepEnded} =
    useTrackAuthStepEvent(mode);
  const {pressed: trackButtonPressed} = useTrackButtonEvent();

  const authConfig = AUTH_MAPPINGS[mode];

  const flowEvent = AnalyticsService.getFlow(authConfig.flowName) || {};

  useEffect(() => {
    return () => {
      resetOtpModal();
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      resetOtpModal();
    }
  }, [isOpen]);

  const resetOtpModal = () => {
    setIsResendCodeLoading(false);
    setIsResendCodeSuccess(false);
    setIsLoadingCodeVerify(false);
    setError('');
    setCode('');
  };

  const resendCode = async () => {
    trackButtonPressed({
      button_name: authConfig.resendCodeButtonName,
      screen_name: authConfig.otpScreenName,
      flow_id: flowEvent.flow_id || '',
      flow_name: flowEvent.flow_name || '',
      triggered_flow_id: '',
      triggered_flow_name: '',
      screen_presentation_id: '',
      campaign_id: '',
      creator_id: '',
      tab: '',
    });
    const processId = uuid();
    const stepEventData: AuthStepEventData = {
      flow_id: flowEvent.flow_id || '',
      process_id: processId,
      provider: 'fortress',
      request_type: 'send_otp',
      input_value: code,
    };

    trackAuthStepStarted({
      ...stepEventData,
      number_of_required_fields: 0,
      number_of_fields_completed: 0,
    });

    setIsResendCodeLoading(true);
    setIsResendCodeSuccess(false);
    setError('');
    resetOtpInputs();
    try {
      const newCaptchaToken = await refreshCaptchaToken?.(true);
      const effectiveCaptchaToken = newCaptchaToken || captchaToken;
      if (!effectiveCaptchaToken) {
        trackAuthStepEnded({
          ...stepEventData,
          reason: reasons.AUTH.FAILURE,
          error: 'missing captchaToken',
        });
        return;
      }
      await Fortress.generateOTP(
        email,
        fortressFlowName,
        effectiveCaptchaToken
      );
      setIsResendCodeSuccess(true);
    } catch (e: any) {
      setError(e?.message);
    }
    setIsResendCodeLoading(false);
  };

  const onVerifyButtonClick = async (otpCode: string): Promise<void> => {
    if (otpCode.length !== 6) {
      return;
    }
    trackButtonPressed({
      button_name: authConfig.continueButtonName,
      screen_name: authConfig.otpScreenName,
      flow_id: flowEvent.flow_id || '',
      flow_name: flowEvent.flow_name || '',
      triggered_flow_id: '',
      triggered_flow_name: '',
      screen_presentation_id: '',
      campaign_id: '',
      creator_id: '',
      tab: '',
    });
    const processId = uuid();
    const stepEventData: AuthStepEventData = {
      flow_id: flowEvent.flow_id || '',
      process_id: processId,
      provider: 'fortress',
      request_type: 'verify_otp',
      input_value: code,
    };

    trackAuthStepStarted({
      ...stepEventData,
      number_of_required_fields: 1,
      number_of_fields_completed: 1,
    });

    const verifySession: VerifyOTPAndSaveTokenCookiesResponse | null =
      await onVerifyCode(otpCode, stepEventData);
    if (verifySession?.token) {
      await authenticateCallback(verifySession, stepEventData);
    }
  };

  const onVerifyCode = async (
    otpCode: string,
    stepEventData: AuthStepEventData
  ): Promise<VerifyOTPAndSaveTokenCookiesResponse | null> => {
    let tokens = null;
    try {
      setError('');
      setIsLoadingCodeVerify(true);
      tokens = await Fortress.verifyOTPAndSaveTokenCookies(email, otpCode);
    } catch (e: any) {
      setError(e?.message);
      setIsLoadingCodeVerify(false);

      trackAuthStepEnded({
        ...stepEventData,
        reason: reasons.AUTH.FAILURE,
        error: e?.message || '',
      });
    }
    return tokens;
  };

  const resetOtpInputs = () => {
    otpInputs.forEach((element: HTMLInputElement) => {
      element.value = ''; // eslint-disable-line no-param-reassign
    });
    setCode('');
    otpInputs?.[0].focus();
  };

  const onModalClose = () => {
    trackButtonPressed({
      button_name: authConfig.closeModalButtonName,
      screen_name: authConfig.otpScreenName,
      flow_id: flowEvent.flow_id || '',
      flow_name: flowEvent.flow_name || '',
      triggered_flow_id: '',
      triggered_flow_name: '',
      screen_presentation_id: '',
      campaign_id: '',
      creator_id: '',
      tab: '',
    });
    setIsModalOpen(false);

    AnalyticsService.screenDismissed(
      authConfig.otpScreenName,
      authConfig.screenName
    );
    AnalyticsService.screenPresented(
      authConfig.screenName,
      authConfig.otpScreenName
    );
    refreshCaptchaToken?.(true);
  };

  const renderTitle = (
    <div className={styles.otpHeader}>
      <Button
        testID="close-modal-button"
        appearance="neutral"
        mode="plain"
        size="large"
        className={styles.otpCloseIcon}
        onClick={onModalClose}
      >
        <Icon
          size="large"
          appearance="neutral"
          name="Actions-Patch-Cancel-Line"
        />
      </Button>
      <Headline size={isMobile ? 'md' : 'xl'}>
        {translate('components.auth.otp-verification-modal.title')}
      </Headline>
    </div>
  );

  return (
    <ModalAuth
      testID={testID}
      open={isOpen}
      modalTitle={renderTitle}
      onClose={onModalClose}
    >
      <div className={styles.otpContent}>
        <Body
          size={isMobile ? 'lg' : 'xl'}
          color={designSystemToken('semantic.fg.secondary')}
        >
          {translate('components.auth.otp-verification-modal.email-label')}
        </Body>
        <Body
          size={isMobile ? 'lg' : 'xl'}
          color={designSystemToken('semantic.fg.secondary')}
        >
          {email}
        </Body>
        <div>
          <OtpInput
            onTypingCode={setCode}
            setOtpInputElements={setOtpInputs}
            onSubmit={onVerifyButtonClick}
          />
          <Body
            size="sm"
            className={styles.otpError}
            color={designSystemToken('semantic.fg.danger')}
            dangerouslyInnerHTML={error}
          />
        </div>
        <div className={styles.otpContinueButton}>
          <Button
            type="submit"
            appearance={mainButtonAppearance}
            mode="filled"
            size="large"
            disabled={code.length < 6}
            onClick={() => onVerifyButtonClick(code)}
            isLoading={isLoadingCodeVerify}
            fullWidth
          >
            {translate('components.auth.otp-verification-modal.continue')}
          </Button>
        </div>
        <Button
          appearance="neutral"
          mode="plain"
          size="large"
          onClick={resendCode}
          className={`${styles.resendCodeButton} ${product === 'content-lab' ? styles.contentLabResendButton : ''}`}
          isLoading={isResendCodeLoading}
          disabled={isResendCodeLoading || !captchaToken}
        >
          {translate('components.auth.otp-verification-modal.resend-code')}
        </Button>
        <div className={styles.otpCodeResendSuccess}>
          {isResendCodeSuccess ? (
            <>
              <Icon
                size="small"
                appearance="neutral"
                name="Actions-Patch-Accept-Line"
              />
              <Body size="sm">
                {translate(
                  'components.auth.otp-verification-modal.resend-code-success'
                )}
              </Body>
            </>
          ) : null}
        </div>
      </div>
    </ModalAuth>
  );
}

export default OtpVerificationModal;
